import React, { useState, useCallback, useContext } from 'react';
import { useQuery } from '@apollo/client';
import { isVatEnabledForCategoryType } from 'src/RootComponents/Product/ProductRoot';
import { ProductType, StoreState } from 'src/types/global-types';
//import { QuickQuote } from 'src/elements/QuickQuote/QuickQuote';
import { TierPrice } from 'src/components/ProductDetail/TieredPricing';
import { shallowEqual, useSelector } from 'react-redux';
import { currentUrl } from 'src/util/currentUrl';
import productShippingGroupsQuery from 'src/queries/productShippingGroup.graphql';
import { ProductOptionsContext } from 'src/components/ProductDetail/ProductOptionsProvider';
//import camelcaseKeys from 'camelcase-keys';
//import { getCategoryDetails } from 'src/components/ProductDetail/ProductOptions';
//import { BulkOrderBlock } from 'src/components/ProductDetail/BulkOrderBlock';
import { AggregationQueryContext } from 'src/components/CategoryListingPage/AggregationQueryProvider';
import { getUrl } from 'src/components/ProductDetail/utils/getUrl';
import Bugsnag from '@bugsnag/js';
import { toNotifiableError } from 'src/util/general';

import { useDeps } from '../../hooks/useDeps';
import { customAttributeMetadata } from '../../queries/customAttributeMetadata.graphql';
import { ConfigContext } from '../AppShell/ConfigProvider';
import { useKlaviyoViewedProduct } from '../../features/klaviyo/hooks/useKlaviyoViewedProduct';

//import { ProductUsp } from './ProductUsp';
import { ProductDetailLayout } from './ProductDetailLayout';
import { useInitialGalleryMedia } from './hooks/useInitialGalleryMedia';
import { ProductOptions } from './ProductOptions';
import { Product, qtyOnePrice } from './utils/product-detail.types';
import { useProductDetailGtm } from './hooks/useProductDetailGtm';
import { ProductSchema } from './ProductStructuredData';
import { ProductTimeDelivery } from './ProductTimeDelivery';
import classes from './ProductDetail.scss';

export type ProductDetailProps = {
    product: Product;
    isVatEnabledForCategory: isVatEnabledForCategoryType;
    beforeReviews?: React.ReactNode;
};

export type ProductShippingGroups = {
    productDetail: {
        sku: string;
        shipperhq_shipping_group: string;
        cutoff_time: string[];
    }[];
};

export const DEFAULT_CUTOFF_TIME = ['18', '00'];

const formatString = (string: string | undefined) => {
    if (!string) return;
    const separator = string.includes('-') ? '-' : string.includes('/') ? '/' : ' ';
    const joinString = separator === ' ' ? ' ' : ` ${separator} `;

    const lowercaseString = string.toLowerCase();
    const stringArray = lowercaseString.split(separator);

    const formattedStringArray = stringArray.map((word) => word.charAt(0).toUpperCase() + word.slice(1));
    return formattedStringArray.join(joinString);
};

const cutoffTimeIncluded = (selectedCutoffTimes: string[][], cutoffTime: string[] | undefined) => {
    if (!cutoffTime) return false;

    return selectedCutoffTimes.some((selectedTime) => {
        if (selectedTime[0] !== cutoffTime[0] || selectedTime[1] !== cutoffTime[1]) {
            return false;
        }

        const selectedHasSeconds = selectedTime.length > 2;
        const cutoffHasSeconds = cutoffTime.length > 2;

        if (selectedHasSeconds && cutoffHasSeconds) {
            return selectedTime[2] === cutoffTime[2];
        }
        if (selectedHasSeconds !== cutoffHasSeconds) {
            return false;
        }

        return true;
    });
};

const removeCutoffTime = (selectedCutoffTimes: string[][], cutoffTime: string[] | undefined) => {
    if (!cutoffTime) return selectedCutoffTimes;

    const newArray = [...selectedCutoffTimes];
    const indexToRemove = newArray.findIndex((selectedTime) => cutoffTimeIncluded([selectedTime], cutoffTime));

    if (indexToRemove !== -1) {
        newArray.splice(indexToRemove, 1);
    }

    return newArray;
};

const getEarliestCutoffTime = (selectedCutoffTimes: string[][]) => {
    if (selectedCutoffTimes.length === 0) return DEFAULT_CUTOFF_TIME;
    if (selectedCutoffTimes.length === 1) return selectedCutoffTimes[0];

    return selectedCutoffTimes.reduce((earliest, current) => {
        const earliestHour = parseInt(earliest[0], 10);
        const currentHour = parseInt(current[0], 10);

        if (currentHour < earliestHour) return current;
        if (currentHour > earliestHour) return earliest;

        const earliestMinute = parseInt(earliest[1], 10);
        const currentMinute = parseInt(current[1], 10);

        if (currentMinute < earliestMinute) return current;
        if (currentMinute > earliestMinute) return earliest;

        const earliestSecond = earliest.length > 2 ? parseInt(earliest[2], 10) : 0;
        const currentSecond = current.length > 2 ? parseInt(current[2], 10) : 0;

        return currentSecond < earliestSecond ? current : earliest;
    });
};

const selector = (s: StoreState) => s.router.location.pathname;

export const ProductDetail: React.FC<ProductDetailProps> = ({ product, isVatEnabledForCategory, beforeReviews }) => {
    const { cutOffTime: cutOffTimeConfig, superFastCutOffTime } = useContext(ConfigContext);
    const cutOffTime = product.cutoff_time || cutOffTimeConfig;
    const [selectedShippingGroups, setSelectedShippingGroups] = useState<string[]>(
        product.shippingType ? [product.shippingType] : [],
    );
    const [selectedCutoffTimes, setSelectedCutoffTimes] = useState<string[][]>([cutOffTime || DEFAULT_CUTOFF_TIME]);
    const [parentShippingGroupUsed, setParentShippingGroupUsed] = useState(true);
    const [parentCutoffTimeUsed, setParentCutoffTimeUsed] = useState(true);
    const [skuQuantities, setSkuQuantities] = useState<Record<string, number>>({});
    const pathname = useSelector(selector, shallowEqual);
    const { DOMAIN } = useDeps().env;
    const url = currentUrl(DOMAIN!, pathname);
    const { materialAggregation, fitAggregation } = useContext(AggregationQueryContext);
    const material = materialAggregation.find((aggItem) => aggItem.value === product.material?.toString())?.label;
    const fitStyle = fitAggregation.find((aggItem) => aggItem.value === product.filter_body_fit?.toString())?.label;

    const { sizes } = useContext(ProductOptionsContext);
    const simpleProductsIds = sizes.map((size) => size.simpleProductId);

    const { data } = useQuery<ProductShippingGroups>(productShippingGroupsQuery, {
        variables: { ids: simpleProductsIds },
        ssr: useDeps().env.SSR_GQL,
    });

    const { data: metaData } = useQuery(customAttributeMetadata, {
        ssr: useDeps().env.SSR_GQL,
    });

    const resetShippingGroups = () => {
        if (product.shippingType) {
            setSelectedShippingGroups([product.shippingType]);
        } else {
            setSelectedShippingGroups([]);
        }
        setParentShippingGroupUsed(true);
        setSelectedCutoffTimes([cutOffTime || DEFAULT_CUTOFF_TIME]);
        setParentCutoffTimeUsed(true);
        setSkuQuantities({});
    };

    //const camelizedCategories = camelcaseKeys(product.categories, { deep: true }) as CamelizedCategory[];
    //const categoryDetailsForBulkBlock = getCategoryDetails(camelizedCategories);
    const formattedBrandName = formatString(product.brand_badge);

    const simpleProductsShippingGroups = data?.productDetail;

    const selectCorrectShippingMethods = (sku: string, value: number) => {
        const shippingGroup = simpleProductsShippingGroups?.find((group) => group.sku === sku);
        const shippingGroupCode = shippingGroup?.shipperhq_shipping_group;
        const shippingGroupCutoffTime = shippingGroup?.cutoff_time || cutOffTime || DEFAULT_CUTOFF_TIME;

        const previousQty = skuQuantities[sku] || 0;
        let newQty: number;

        if (value === -1) {
            newQty = Math.max(0, previousQty - 1);
        } else {
            newQty = Math.max(0, value);
        }

        const diff = newQty - previousQty;

        setSkuQuantities((prev) => ({ ...prev, [sku]: newQty }));

        if (diff === 0) return;

        let newSelectedShippingGroups = [...selectedShippingGroups];
        let newSelectedCutoffTimes = [...selectedCutoffTimes];

        if (parentShippingGroupUsed) {
            newSelectedShippingGroups = [];
            setParentShippingGroupUsed(false);
        }

        if (parentCutoffTimeUsed) {
            newSelectedCutoffTimes = [];
            setParentCutoffTimeUsed(false);
        }

        const shippingCodeToUse = shippingGroupCode || product.shippingType;

        if (diff < 0) {
            const itemsToRemove = Math.abs(diff);

            for (let i = 0; i < itemsToRemove; i++) {
                if (shippingCodeToUse && newSelectedShippingGroups.includes(shippingCodeToUse)) {
                    const codeIndex = newSelectedShippingGroups.indexOf(shippingCodeToUse);
                    if (codeIndex !== -1) {
                        newSelectedShippingGroups.splice(codeIndex, 1);
                    }
                }

                if (cutoffTimeIncluded(newSelectedCutoffTimes, shippingGroupCutoffTime)) {
                    newSelectedCutoffTimes = removeCutoffTime(newSelectedCutoffTimes, shippingGroupCutoffTime);
                }
            }
        }

        if (diff > 0) {
            for (let i = 0; i < diff; i++) {
                if (shippingCodeToUse) {
                    newSelectedShippingGroups.push(shippingCodeToUse);
                }

                if (shippingGroupCutoffTime) {
                    newSelectedCutoffTimes.push(shippingGroupCutoffTime);
                }
            }
        }

        if (newSelectedShippingGroups.length <= 0) {
            if (product.shippingType) {
                setSelectedShippingGroups([product.shippingType]);
            } else {
                setSelectedShippingGroups([]);
            }
            setParentShippingGroupUsed(true);
        }

        if (newSelectedShippingGroups.length > 0) {
            setSelectedShippingGroups(newSelectedShippingGroups);
        }

        if (newSelectedCutoffTimes.length <= 0) {
            setSelectedCutoffTimes([cutOffTime || DEFAULT_CUTOFF_TIME]);
            setParentCutoffTimeUsed(true);
        }

        if (newSelectedCutoffTimes.length > 0) {
            setSelectedCutoffTimes(newSelectedCutoffTimes);
        }
    };

    /**
     * GTM
     */
    useProductDetailGtm(product, qtyOnePrice(product.margin_tier_prices), url);

    /**
     * Klaviyo - Viewed Product tracking
     */
    useKlaviyoViewedProduct({ product, price: qtyOnePrice(product.margin_tier_prices), url });

    const [selectedPrice, setSelectedPrice] = useState(qtyOnePrice(product.margin_tier_prices));
    const [selectedQuantity, setSelectedQuantity] = useState(0);

    const showPrice =
        !product.can_customise ||
        product.type === ProductType.BundleProduct ||
        product.type === ProductType.WearerBundlesProduct ||
        product.margin_tier_prices.length === 1;

    const priceChanged = useCallback((price: TierPrice) => {
        setSelectedPrice({
            ...price,
            regular_price_incl: 0,
            regular_price: 0,
            final_price_incl: 0,
            final_price: 0,
        });
    }, []);

    const quantityChanged = (quantity: number) => {
        setSelectedQuantity(quantity);
    };

    const productVideo = product.media_gallery_entries.filter((media) => media.video_content !== null);
    const galleryMedia = useInitialGalleryMedia(product.media_entries);

    let logoUrl = null;
    try {
        logoUrl = require(`../../../static/img-brands/Logos/${getUrl(product.brand_badge)}`);
    } catch (err) {
        Bugsnag.notify(toNotifiableError(err));
        logoUrl = null;
    }

    return (
        <ProductDetailLayout
            product={product}
            showSku={true}
            showPrice={showPrice}
            media_entries={galleryMedia}
            productVideo={productVideo}
            price={selectedPrice || qtyOnePrice(product.margin_tier_prices)}
            isBundle={false}
            quantity={selectedQuantity}
            isRegularView={true}
            beforeReviews={beforeReviews}
        >
            <ProductSchema
                product={product}
                productPrice={qtyOnePrice(product.margin_tier_prices)}
                material={material}
                fitStyle={fitStyle}
                brandName={formattedBrandName}
                logoUrl={logoUrl ? `https://${DOMAIN}/${logoUrl}` : undefined}
            />
            <ProductOptions
                product={product}
                isVatEnabledForCategory={isVatEnabledForCategory}
                priceChanged={priceChanged}
                quantityChanged={quantityChanged}
                selectCorrectShippingMethods={selectCorrectShippingMethods}
                simpleProductsShippingGroups={simpleProductsShippingGroups}
                metaData={metaData}
                resetShippingGroups={resetShippingGroups}
            />
            <div>
                {product.can_customise && (
                    <p className={classes.addLogoPrompt}>
                        <img src={require('src/components/Svg/svgs/add-your-logo.svg')} alt="Check icon" />
                        Add item to basket to add your logo
                    </p>
                )}
                <div className={classes.productDetailTrustpilot}>
                    <span className={classes.trustpilotBrand}>
                        <span className={classes.trustpilotBrandStar} aria-hidden="true">
                            &#9733;
                        </span>
                        Trustpilot
                    </span>
                    <span className={classes.trustpilotStars} aria-hidden="true">
                        <span>&#9733;</span>
                        <span>&#9733;</span>
                        <span>&#9733;</span>
                        <span>&#9733;</span>
                        <span className={classes.trustpilotStarMuted}>&#9733;</span>
                    </span>
                    <strong className={classes.trustpilotRating}>4.2</strong>
                    <span className={classes.trustpilotReviews}>(10,982 Reviews)</span>
                </div>
                <ProductTimeDelivery
                    shippingTypes={selectedShippingGroups}
                    isCustomisable={product.can_customise}
                    productUrl={product.url_key || ''}
                    cutOffTime={getEarliestCutoffTime(selectedCutoffTimes)}
                    superFastCutOffTime={superFastCutOffTime}
                />

                {/* <ProductUsp />
                {categoryDetailsForBulkBlock && (
                    <BulkOrderBlock
                        sku={product.sku}
                        categoryDetails={categoryDetailsForBulkBlock}
                        categoryList={metaData?.customAttributeMetadata?.items?.[3].attribute_options}
                    />
                )}
                <QuickQuote sku={product.sku} /> */}
            </div>
        </ProductDetailLayout>
    );
};
